import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import toast from 'react-hot-toast';

const { FiExternalLink, FiSearch } = FiIcons;

const toSlug = (s) => (s || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9\s-_]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-');

const getRouteServiceId = (svc) => {
  const cand = svc?.slug || svc?.code || svc?.key;
  if (cand && cand.toString().trim()) return cand.toString().trim();
  return toSlug(svc?.name || 'service');
};

export default function AdminServiceLinks() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('services_dy3xr')
          .select('*')
          .order('category', { ascending: true })
          .order('name', { ascending: true });
        if (error) throw error;
        setServices(data || []);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load services');
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const getKey = (s) => (s?.slug || s?.code || s?.key || toSlug(s?.name || ''))
    .toString()
    .trim()
    .toLowerCase();

  const deduped = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of services || []) {
      const k = getKey(s);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(s);
      }
    }
    return out;
  }, [services]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = deduped;
    if (!term) return base;
    return base.filter((s) => {
      const hay = `${s.name} ${s.category} ${s.slug || ''} ${s.code || ''} ${s.key || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [q, deduped]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  const grouped = filtered.reduce((acc, s) => {
    const cat = s.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Dynamic Service Links</h1>
          <p className="text-gray-600">Open live dynamic pages to edit via the inline editor.</p>
        </motion.div>

        <div className="relative mb-6">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search services..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="space-y-10">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h2 className="text-lg font-semibold text-gray-800 mb-3 capitalize">{cat.replace('-', ' ')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((s) => {
                  const sid = getRouteServiceId(s);
                  return (
                    <Card key={s.id} className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{s.name}</div>
                        <div className="text-xs text-gray-500">{sid}</div>
                      </div>
                      <a
                        href={`#/service-details/${sid}?preview=1`}
                        className="inline-flex items-center text-primary-600 hover:text-primary-700"
                        title="Open live dynamic page"
                      >
                        <span className="mr-2">Open</span>
                        <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                      </a>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
